<template>
  <div>
    <!-- Hero -->
    <section class="relative bg-hero-fade pt-20 pb-14 lg:pt-28">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-4xl text-center">
        <div class="eyebrow mx-auto w-fit mb-4">SEO — Search Engine Optimization</div>
        <h1 class="font-display text-5xl lg:text-6xl text-ink-900 leading-[1.05] text-balance">
          Most SEO tools hand you <span class="italic text-brand-700">200 issues</span><br />
          and a shrug. We rank them by what moves your ranking.
        </h1>
        <p class="mt-6 text-lg text-ink-600 max-w-2xl mx-auto text-pretty">
          LeadRanks runs the same depth of technical, content, and authority analysis an enterprise agency audit covers — then ranks every finding by estimated impact, so the highest-leverage fix is always listed first, not just the easiest one.
        </p>
        <div class="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <router-link to="/analyze" class="btn-brand btn-lg">Run a free SEO audit <Icon name="arrow-right" :size="16" /></router-link>
          <router-link to="/pricing" class="btn btn-lg btn-outline">See plans</router-link>
        </div>
      </div>
    </section>

    <!-- What is SEO -->
    <section class="py-20 lg:py-24 border-b border-ink-100">
      <div class="container-wide max-w-5xl">
        <div class="max-w-2xl mb-10">
          <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-700 bg-brand-50 px-3 py-1 rounded-full border border-brand-100 mb-4">
            <Icon name="search" :size="12" /> What is SEO?
          </div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">Four levers, one ranking.</h2>
        </div>
        <p class="text-ink-600 text-lg leading-relaxed max-w-3xl mb-10">
          Search Engine Optimization is the practice of making a page easy for Google's crawler to reach, understand, trust, and load fast — the four levers that actually decide where you land in a results page. Technical health determines whether the crawler can read the page at all. Content quality determines whether it's worth ranking once read. Backlinks and authority determine how much the crawler trusts the domain. Performance determines whether real visitors get a fast experience once they click through. Weakness in any one lever caps what the other three can achieve.
        </p>
        <p class="text-ink-600 text-lg leading-relaxed max-w-3xl">
          SEO shares real ground with <router-link to="/geo-optimization" class="text-brand-700 underline decoration-brand-200 hover:decoration-brand-500 underline-offset-2">GEO and AEO</router-link> — clean structure and genuine authority help with both — but they optimize for different readers. SEO earns you a ranking position a human clicks through to read. GEO/AEO earns you a place inside an AI-generated answer the user may never click through from at all. LeadRanks scores both in the same audit, but this page is specifically about the classic ranking signals: the ones that decide whether you show up in the ten blue links in the first place.
        </p>
      </div>
    </section>

    <!-- What LeadRanks' SEO audit actually checks -->
    <section class="py-20 lg:py-28">
      <div class="container-wide max-w-6xl">
        <div class="text-center max-w-2xl mx-auto mb-14">
          <div class="eyebrow mx-auto w-fit mb-3">The SEO Audit</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">Eight real signal groups, not a guess</h2>
          <p class="mt-4 text-ink-600 text-lg leading-relaxed">17 parallel modules feed into one 100+ point audit — every signal scored independently, then rolled into one score, with a specific fix for each gap, not just a number.</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="c in checks" :key="c.title" class="card card-hover p-6 group">
            <span class="w-10 h-10 rounded-lg bg-ink-50 text-ink-800 flex items-center justify-center mb-4 group-hover:bg-brand-50 group-hover:text-brand-700 transition-colors">
              <Icon :name="c.icon" :size="16" />
            </span>
            <h3 class="text-sm font-semibold text-ink-900 tracking-tight">{{ c.title }}</h3>
            <p class="mt-1.5 text-xs text-ink-600 leading-relaxed">{{ c.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How to fix each signal -->
    <section class="py-20 lg:py-28 bg-ink-50/40 border-y border-ink-100">
      <div class="container-wide max-w-4xl">
        <div class="max-w-2xl mb-12">
          <div class="eyebrow mb-3"><Icon name="sliders" :size="12" /> How to fix each signal</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">The specific work behind each score</h2>
          <p class="mt-4 text-ink-600 text-lg leading-relaxed">Every one of these is a real, scored check in the audit — not general advice. Here's exactly what moves each number.</p>
        </div>
        <div class="space-y-10">
          <div v-for="c in howTo" :key="c.title" class="flex flex-col sm:flex-row gap-5">
            <div class="shrink-0 w-10 h-10 rounded-lg bg-white border border-ink-100 text-ink-800 flex items-center justify-center">
              <Icon :name="c.icon" :size="16" />
            </div>
            <div>
              <h3 class="text-base font-semibold text-ink-900">{{ c.title }}</h3>
              <p class="mt-1.5 text-sm text-ink-600 leading-relaxed">{{ c.detail }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Best practices -->
    <section class="py-20 lg:py-24">
      <div class="container-wide max-w-4xl">
        <div class="max-w-2xl mb-10">
          <div class="eyebrow mb-3"><Icon name="check-circle" :size="12" /> Best practices</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">A working SEO checklist</h2>
        </div>
        <ul class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <li v-for="p in bestPractices" :key="p" class="flex items-start gap-3 text-ink-700 leading-relaxed">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>{{ p }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Example -->
    <section class="py-20 lg:py-24 bg-ink-50/40 border-y border-ink-100">
      <div class="container-wide max-w-4xl">
        <div class="max-w-2xl mb-8">
          <div class="eyebrow mb-3"><Icon name="align-left" :size="12" /> Example</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">A checklist isn't a plan. A priority order is.</h2>
          <p class="mt-4 text-ink-600 text-lg leading-relaxed">A generic, illustrative example — not a real customer case study — of the difference impact ranking makes.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div class="card p-6 border border-danger-100">
            <div class="badge badge-danger mb-3">Flat checklist tool</div>
            <p class="text-sm text-ink-600 leading-relaxed">47 issues found, listed in the order the crawler happened to hit them: a missing alt tag on item #1, a missing canonical tag buried at #31, three duplicate meta descriptions at #44-46.</p>
            <p class="mt-3 text-xs text-ink-500">You're left to guess which of the 47 is actually worth your afternoon. Most people start at #1 and lose momentum before reaching what matters.</p>
          </div>
          <div class="card p-6 border border-accent-100">
            <div class="badge badge-accent mb-3">LeadRanks, ranked by impact</div>
            <p class="text-sm text-ink-600 leading-relaxed">Same 47 issues. #1: the missing canonical tag, splitting ranking signals across duplicate URLs — highest estimated impact. #2: 3.1s LCP on the product page with the most organic traffic. The alt tag lands at #39.</p>
            <p class="mt-3 text-xs text-ink-500">Same findings, reordered by what actually costs you rankings and revenue — so the first hour of work is the highest-leverage hour, not whatever the crawler found first.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How to run an SEO audit -->
    <section class="py-20 lg:py-24">
      <div class="container-wide max-w-3xl">
        <div class="max-w-2xl mb-10">
          <div class="eyebrow mb-3"><Icon name="target" :size="12" /> How to run an SEO audit</div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">From URL to fixed, in four steps</h2>
        </div>
        <ol class="space-y-6">
          <li v-for="(s, i) in howToSteps" :key="s.title" class="flex gap-5">
            <span class="shrink-0 w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-semibold">{{ i + 1 }}</span>
            <div>
              <h3 class="text-base font-semibold text-ink-900">{{ s.title }}</h3>
              <p class="mt-1 text-sm text-ink-600 leading-relaxed">{{ s.detail }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- Honesty section: what this is, and what it isn't -->
    <section class="py-20 bg-ink-50/60 border-y border-ink-100">
      <div class="container-wide max-w-4xl">
        <div class="card p-8 lg:p-10">
          <div class="flex items-start gap-3">
            <Icon name="info" :size="20" class="text-brand-600 mt-0.5 shrink-0" />
            <div>
              <h3 class="font-semibold text-lg text-ink-900">What this score measures</h3>
              <p class="mt-2 text-ink-600 leading-relaxed">
                The SEO score evaluates the structural, content, authority, and performance signals within your control — the ones a fix either resolves or it doesn't, confirmed on re-scan. It's not a promise of a specific ranking position: where you land also depends on your competitors' own signals and Google's algorithm, neither of which any audit tool controls or can honestly predict. What LeadRanks gives you is the same kind of leading indicator SEO has always run on — fix the signals within your control, ranked by real impact, and you improve your odds against everyone still working off an unordered checklist.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-24">
      <div class="container-tight max-w-3xl">
        <div class="text-center mb-10">
          <div class="eyebrow mx-auto w-fit mb-3">FAQ</div>
          <h2 class="font-display text-3xl text-ink-900">Common questions about SEO</h2>
        </div>
        <div class="divide-y divide-ink-100 border border-ink-100 rounded-2xl overflow-hidden">
          <details v-for="(faq, i) in faqs" :key="i" class="group p-5 lg:p-6">
            <summary class="flex items-center justify-between cursor-pointer list-none">
              <span class="text-base font-medium text-ink-900">{{ faq.question }}</span>
              <Icon name="chevron-down" :size="16" class="text-ink-400 group-open:rotate-180 transition-transform shrink-0 ml-4" />
            </summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed">{{ faq.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <!-- Sources -->
    <section class="pb-16">
      <div class="container-tight max-w-3xl">
        <p class="text-2xs text-ink-400 leading-relaxed">
          Sources:
          <a href="https://developers.google.com/search/docs/fundamentals/seo-starter-guide" target="_blank" rel="noopener noreferrer" class="underline decoration-ink-200 hover:text-ink-600 hover:decoration-ink-400 underline-offset-2">Google Search Central — SEO Starter Guide</a>,
          <a href="https://web.dev/articles/vitals" target="_blank" rel="noopener noreferrer" class="underline decoration-ink-200 hover:text-ink-600 hover:decoration-ink-400 underline-offset-2">web.dev — Core Web Vitals</a>, and
          <a href="https://schema.org/" target="_blank" rel="noopener noreferrer" class="underline decoration-ink-200 hover:text-ink-600 hover:decoration-ink-400 underline-offset-2">Schema.org</a>.
        </p>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-28">
      <div class="container-tight">
        <div class="card p-10 lg:p-14 text-center bg-ink-900 border border-ink-800 relative overflow-hidden">
          <div class="absolute inset-0 bg-dot-ink bg-dot-20 opacity-[0.05]"></div>
          <div class="absolute -top-20 -right-20 w-72 h-72 bg-brand-500/20 rounded-full blur-3xl"></div>
          <div class="relative">
            <h2 class="font-display text-4xl text-white">See your SEO score in 3–5 minutes.</h2>
            <p class="mt-3 text-ink-300 max-w-lg mx-auto">Free audit, no credit card. Get your score across all eight signal groups plus the fix for each gap, ranked by impact.</p>
            <div class="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <router-link to="/analyze" class="btn-brand btn-lg">Run a free SEO audit <Icon name="arrow-right" :size="16" /></router-link>
              <router-link to="/features" class="btn btn-lg bg-white/10 text-white border border-white/20 hover:bg-white/20">See all features</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';

const TITLE = 'SEO Optimization — Audit & Fix Priority | LeadRanks';
const DESC = 'LeadRanks runs a 100+ point SEO audit across technical health, content quality, backlinks, and performance — every finding ranked by real impact.';

const checks = [
  { icon: 'code',      title: 'Technical Site Health', body: 'Crawl health, structured data, security headers, mobile rendering — 17 parallel modules, all at once.' },
  { icon: 'book',      title: 'Content Quality',       body: 'Readability, keyword density, heading hierarchy, semantic coverage — does the content actually answer the query?' },
  { icon: 'link',      title: 'Backlink & Authority',  body: 'Referring domains, anchor text distribution, toxic link detection — who links to you, and who links to them.' },
  { icon: 'shield',    title: 'Competitor Gap',        body: 'Up to 3 competitors audited side-by-side — keywords they rank for that you don\'t, gaps you can close today.' },
  { icon: 'trending-up', title: 'Core Web Vitals',     body: 'LCP, FCP, CLS, TBT — every metric Google uses, with clear pass/fail thresholds.' },
  { icon: 'zap',       title: 'PageSpeed Insights',    body: 'Lab data plus real-user field data (CrUX). Mobile and desktop, side by side.' },
  { icon: 'image',     title: 'Image Optimization',    body: 'Format (WebP/AVIF), compression, lazy loading, alt text, responsive sources.' },
  { icon: 'activity',  title: 'Revenue Impact',        body: 'Every issue tagged with estimated traffic and revenue cost — so you can prove the fix before you make it.' },
];

const howTo = [
  { icon: 'code',      title: 'Technical Site Health', detail: 'Fix crawl errors and broken internal links first — nothing downstream matters if the crawler can\'t reach the page. Add valid structured data only for content that\'s genuinely present, and confirm mobile rendering isn\'t hiding content behind a broken viewport or unplayable interactive element.' },
  { icon: 'book',      title: 'Content Quality',       detail: 'Match content depth and structure to what\'s actually ranking for the query today, not what ranked two years ago. Use a real heading hierarchy — one H1, then H2s for major sections — and cover the semantic ground a searcher expects, not just the exact keyword phrase.' },
  { icon: 'link',      title: 'Backlink & Authority',  detail: 'Prioritize earning links from sites with genuine topical relevance and authority over volume. Disavow or clean up toxic links dragging the profile down, and diversify anchor text — a profile that\'s 80% exact-match anchor reads as manipulated, not popular.' },
  { icon: 'shield',    title: 'Competitor Gap',        detail: 'Target the keywords competitors rank for that you don\'t before chasing net-new terms — you\'re not starting from zero relevance on a topic your site is already adjacent to.' },
  { icon: 'trending-up', title: 'Core Web Vitals',     detail: 'LCP is almost always the hero image or a render-blocking script — fix that first. CLS usually traces to images or ads without a reserved size, shifting layout as they load.' },
  { icon: 'zap',       title: 'PageSpeed Insights',    detail: 'Trust field data (real visitors) over lab data (a single simulated run) when the two disagree — field data reflects what your actual traffic experiences, including slower real-world networks and devices.' },
  { icon: 'image',     title: 'Image Optimization',    detail: 'Convert hero and above-the-fold images to WebP or AVIF, lazy-load everything below the fold, and write real, descriptive alt text — not a keyword-stuffed placeholder.' },
  { icon: 'activity',  title: 'Revenue Impact',        detail: 'Fix the issues on your highest-traffic pages first, even if a lower-traffic page has a "worse" technical score in isolation — the same fix is worth more where more visitors are already arriving.' },
];

const bestPractices = [
  'Fix crawl errors and broken internal links before anything else',
  'One logical heading hierarchy — H1 once, then H2s, then nested H3s',
  'Real, descriptive alt text — never a keyword-stuffed placeholder',
  'Convert hero and above-the-fold images to WebP or AVIF',
  'Earn links from topically relevant, genuinely authoritative sources',
  'Diversify anchor text — an all-exact-match profile reads as manipulated',
  'Trust real-user field data over a single simulated PageSpeed run',
  'Target competitor keyword gaps before chasing net-new terms',
  'Reserve layout space for images and ads to prevent CLS shifts',
  'Fix the highest-traffic pages first — same fix, more impact',
];

const howToSteps = [
  { title: 'Run a free audit', detail: 'Enter your URL — LeadRanks scans 100+ SEO signals across all eight groups in one pass, no credit card required.' },
  { title: 'Review your SEO score', detail: 'See all eight signal groups scored independently, each with a plain-language explanation of what\'s missing.' },
  { title: 'Fix issues in priority order', detail: 'The report ranks every finding by estimated impact, so the highest-leverage fix is always listed first — not just the easiest one.' },
  { title: 'Re-scan to confirm', detail: 'Structural and technical signals are either present or they aren\'t. Re-running the audit confirms the change actually registered.' },
];

const faqs = [
  { question: 'What does LeadRanks\' SEO audit actually check?',
    answer: 'Eight signal groups feeding into one 100+ point score: technical site health (17 parallel modules), content quality, backlink and authority profile, competitor keyword gaps, Core Web Vitals, PageSpeed Insights, image optimization, and revenue impact scoring. Each group is scored independently and shown in your report with a specific fix for anything scoring low.' },
  { question: 'How is this different from a generic SEO checklist tool?',
    answer: 'A checklist tool lists every issue it finds in whatever order its crawler happened to hit them. LeadRanks ranks every finding by estimated ranking and revenue impact instead, so the highest-leverage fix is always at the top of your report — not buried at issue #31 because that\'s where the crawler found it.' },
  { question: 'Is SEO the same as GEO or AEO?',
    answer: 'Related, not identical. SEO earns you a ranking position a human clicks through to read. GEO and AEO (Generative/Answer Engine Optimization) earn you a place inside an AI-generated answer, often with no click-through at all. Good technical structure and real authority help both, but they\'re scored as separate signals in your report — see the full breakdown on the GEO/AEO page.' },
  { question: 'Can you guarantee a #1 ranking?',
    answer: 'No, and be skeptical of anyone who claims otherwise. Your ranking also depends on your competitors\' own signals and Google\'s algorithm, neither of which any tool controls. What LeadRanks measures and improves is everything within your control — fix those, ranked by real impact, and you improve your odds against sites still working off an unordered checklist.' },
  { question: 'How long does a full audit take?',
    answer: 'A full 100+ point audit typically finishes in 3–5 minutes, running all 17 analysis modules in parallel rather than one at a time.' },
  { question: 'Do I need to fix every issue the audit finds?',
    answer: 'No — that\'s exactly what impact ranking is for. Most sites have a handful of high-impact issues and a long tail of minor ones. Fix the top few first, re-scan to confirm, and the ranked list tells you what\'s actually worth the next hour of your time.' },
];

usePageMeta({
  title: TITLE,
  meta: [
    { name: 'description', content: DESC },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESC },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://leadranks.io/' },
        { '@type': 'ListItem', position: 2, name: 'SEO Optimization', item: 'https://leadranks.io/seo-optimization' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to run an SEO audit',
      step: howToSteps.map((s) => ({
        '@type': 'HowToStep',
        name: s.title,
        text: s.detail,
      })),
    },
  ],
});
</script>
